import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { handleApiError } from "@/utils/apiErrorHandler";
import { clearError } from "./errorSlice";

export const postMessage = createAsyncThunk(
  "contactPremium/postMessage",
  async (formData: any, {rejectWithValue, dispatch}) => {
    try {
      dispatch(clearError());
      const response = await axios.post(
        `/papi/messages/contact-premium`,
        formData, // Second parameter: the actual data/body
        {
          headers: {
            "Content-Type": "application/json",
          },
        } // Third parameter: config object with headers
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "contactPremium", dispatch);
      return rejectWithValue(error instanceof Error ? error.message : "Failed to submit message");
    }
  }
);

interface ContactPremiumState {
  postMessageResponse: any;
  loading: boolean;
  error: string | null;
  toast: {
    visible: boolean;
    message: string;
    variant: "success" | "error" | "warning" | "info";
  };
}

const initialState: ContactPremiumState = {
  postMessageResponse: null,
  loading: false,
  error: null,
  toast: {
    visible: false,
    message: "",
    variant: "info",
  },
};

const contactPremiumSlice = createSlice({
  name: "contactPremium",
  initialState,
  reducers: {
    showToast: (state, action) => {
      state.toast = {
        visible: true,
        message: action.payload.message || "Operation completed",
        variant: action.payload.variant || "info",
      };
    },
    hideToast: (state) => {
      state.toast.visible = false;
    },
    resetToast: (state) => {
      state.toast = initialState.toast;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Reset toast when starting a new request
        state.toast = initialState.toast;
      })
      .addCase(postMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.postMessageResponse = action.payload;
        // Show success toast
        state.toast = {
          visible: true,
          message: "Your form has been successfully submitted",
          variant: "success",
        };
      })
      .addCase(postMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to submit message";
        // Show error toast
        state.toast = {
          visible: true,
          message:
            "There was an error in submitting your form. Please try again.",
          variant: "error",
        };
      });
  },
});

export const { showToast, hideToast, resetToast } = contactPremiumSlice.actions;
export default contactPremiumSlice.reducer;
